/**
 * Service Worker Registration and Update Management
 * 
 * Implements proper PWA update flow:
 * - Manual registration for better control
 * - Check for updates on every app launch
 * - Use skipWaiting + clientsClaim for immediate updates
 * - Reload on controllerchange
 */

import { registerSW } from 'virtual:pwa-register'

export interface SWUpdateInfo {
  needsUpdate: boolean
  updateAvailable: boolean
  registration?: ServiceWorkerRegistration
}

let updateCheckInterval: number | null = null
let swUpdateCallback: ((info: SWUpdateInfo) => void) | null = null

/**
 * Register service worker and set up update checking
 */
export function registerServiceWorker(
  onUpdateCallback?: (info: SWUpdateInfo) => void
) {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported')
    return null
  }

  swUpdateCallback = onUpdateCallback || null

  // Register the service worker with immediate update detection
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      console.log('[SW] Update available - new version detected')
      
      // Dispatch custom event for the UI to catch
      window.dispatchEvent(new CustomEvent('sw-update-found', {
        detail: { needsUpdate: true, updateAvailable: true }
      }))
      
      if (swUpdateCallback) {
        swUpdateCallback({ 
          needsUpdate: true, 
          updateAvailable: true 
        })
      }
    },
    onOfflineReady() {
      console.log('[SW] App ready to work offline')
    },
    onRegistered(registration) {
      if (!registration) return
      
      console.log(`[SW] Registered. Version: ${__APP_VERSION__}, Build: ${__BUILD_TIME__}`)
      
      // Check for updates on registration
      checkForUpdates(registration)
      
      // Check for updates every 60 seconds
      updateCheckInterval = window.setInterval(() => {
        console.log('[SW] Checking for updates...')
        checkForUpdates(registration)
      }, 60 * 1000)
    },
    onRegisterError(error) {
      console.error('[SW] Registration failed:', error)
    },
  })

  // Reload when new service worker takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('[SW] Controller changed - reloading to use new version')
    window.location.reload()
  })

  return updateSW
}

/**
 * Check for service worker updates
 */
async function checkForUpdates(registration: ServiceWorkerRegistration) {
  try {
    await registration.update()
  } catch (error) {
    console.error('[SW] Update check failed:', error)
  }
}

/**
 * Force update by calling skipWaiting on waiting worker
 */
export async function forceUpdate() {
  const registration = await navigator.serviceWorker.getRegistration()
  
  if (!registration?.waiting) {
    console.log('[SW] No waiting worker to activate')
    return false
  }

  console.log('[SW] Activating waiting service worker...')
  
  // Tell the waiting SW to skip waiting and take control
  registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  
  return true
}

/**
 * Cleanup on app unmount
 */
export function cleanupServiceWorker() {
  if (updateCheckInterval) {
    clearInterval(updateCheckInterval)
    updateCheckInterval = null
  }
}

/**
 * Get current service worker state for debugging
 */
export async function getServiceWorkerState() {
  if (!('serviceWorker' in navigator)) {
    return { supported: false }
  }

  const registration = await navigator.serviceWorker.getRegistration()
  
  return {
    supported: true,
    registered: !!registration,
    controller: !!navigator.serviceWorker.controller,
    waiting: !!registration?.waiting,
    installing: !!registration?.installing,
    active: !!registration?.active,
    updateFound: !!registration?.waiting,
    appVersion: __APP_VERSION__,
    buildTime: __BUILD_TIME__,
  }
}

// Declare global constants injected by Vite
declare const __APP_VERSION__: string
declare const __BUILD_TIME__: string
